import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Grid,
  Container,
  LinearProgress,
  IconButton,
  InputAdornment,
  Fade,
  Chip
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
  // State yönetimi
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redux state
  const userData = useSelector(state => state.loginData.loginData);
  const appToken = useSelector(state => state.appToken.apptoken);

  useEffect(() => {
    // Şifre gücünü ölç
    if (newPassword) {
      let strength = 0;
      // En az 8 karakter
      if (newPassword.length >= 8) strength += 25;
      // Büyük harf içeriyor mu
      if (/[A-Z]/.test(newPassword)) strength += 25;
      // Küçük harf içeriyor mu
      if (/[a-z]/.test(newPassword)) strength += 25;
      // Sayı veya özel karakter içeriyor mu
      if (/[0-9!@#$%^&*(),.?":{}|<>]/.test(newPassword)) strength += 25;
      
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [newPassword]);

  // Şifre karşılaştırma kontrolü
  useEffect(() => {
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordError('Şifreler eşleşmiyor');
    } else {
      setPasswordError('');
    }
  }, [newPassword, confirmNewPassword]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast.error("Yeni şifreler uyuşmuyor.");
      return;
    }

    if (passwordStrength < 75) {
      toast.warning("Lütfen daha güçlü bir şifre seçin.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        "https://app.hhoms.com/api/panel/Users/passwordChange",
        {
          Email: userData.username,
          OldPassword: oldPassword,
          NewPassword: newPassword,
          PasswordConfirm: confirmNewPassword,
        },
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id
          }
        }
      );
      toast.success("Şifre başarıyla değiştirildi.");
      // Formu sıfırla
      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      toast.error("Şifre değiştirme işlemi başarısız oldu.");
      console.error('Şifre değiştirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Şifre güç seviyesi rengi
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'error';
    if (passwordStrength < 50) return 'error';
    if (passwordStrength < 75) return 'warning';
    return 'success';
  };

  // Şifre güç seviyesi metni
  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Çok Zayıf';
    if (passwordStrength < 50) return 'Zayıf';
    if (passwordStrength < 75) return 'Orta';
    return 'Güçlü';
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Profil Başlığı - Email Gösterimi */}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box 
          sx={{ 
            p: 4, 
            display: 'flex', 
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)'
          }}
        >
          <Avatar 
            sx={{ 
              width: 70, 
              height: 70, 
              bgcolor: 'primary.dark',
              border: '3px solid white',
              fontSize: '1.8rem',
              boxShadow: 3
            }}
          >
            <EmailIcon fontSize="large" />
          </Avatar>
          <Box sx={{ ml: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Profil Bilgileri
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" />
              <Typography variant="h6" sx={{ opacity: 0.95 }}>
                {userData?.username || userData?.email || 'email@example.com'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Şifre Değiştirme Bölümü */}
      <Paper elevation={3} sx={{ borderRadius: 3, p: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SecurityIcon color="primary" />
            Şifre Değiştir
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Güvenliğiniz için şifrenizi düzenli olarak değiştirmenizi öneririz.
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3, borderRadius: 2 }} />}

        <form onSubmit={handlePasswordChange}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mevcut Şifre"
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Yeni Şifre"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {newPassword && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 2, fontWeight: 'medium' }}>
                      Şifre Gücü: {getPasswordStrengthText()}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength}
                      color={getPasswordStrengthColor()}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip 
                      size="small" 
                      icon={newPassword.length >= 8 ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                      label="En az 8 karakter" 
                      color={newPassword.length >= 8 ? 'success' : 'default'}
                      variant={newPassword.length >= 8 ? 'filled' : 'outlined'}
                    />
                    <Chip 
                      size="small" 
                      icon={/[A-Z]/.test(newPassword) ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                      label="Büyük harf" 
                      color={/[A-Z]/.test(newPassword) ? 'success' : 'default'}
                      variant={/[A-Z]/.test(newPassword) ? 'filled' : 'outlined'}
                    />
                    <Chip 
                      size="small" 
                      icon={/[a-z]/.test(newPassword) ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                      label="Küçük harf" 
                      color={/[a-z]/.test(newPassword) ? 'success' : 'default'}
                      variant={/[a-z]/.test(newPassword) ? 'filled' : 'outlined'}
                    />
                    <Chip 
                      size="small" 
                      icon={/[0-9!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                      label="Sayı veya özel karakter" 
                      color={/[0-9!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'success' : 'default'}
                      variant={/[0-9!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'filled' : 'outlined'}
                    />
                  </Box>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Yeni Şifreyi Onayla"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                variant="outlined"
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading || !!passwordError || passwordStrength < 75}
                startIcon={<SecurityIcon />}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Şifreyi Değiştir
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default Profile;